import { ReebeloStoreT } from '@lambda/reebelo';

// ---------------------------------------------------------------------------
// Data team: this is the only file you need to touch to update conversion
// tracking factors for the Checkout Completed `adjusted_value` signal sent to
// Google Ads (RT-16227). No other code changes are needed when these change.
//
// Resolution order for a line item's factor (no value is ever left
// unadjusted):
//   1. ADJUSTMENT_FACTORS[store][category]     — exact store + category
//   2. STORE_DEFAULT_ADJUSTMENT_FACTORS[store] — category unknown/null,
//                                                 store known
//   3. CATEGORY_DEFAULT_ADJUSTMENT_FACTORS[category] — store unknown,
//                                                 category known
//   4. GLOBAL_DEFAULT_ADJUSTMENT_FACTOR        — both unknown
// A category explicitly set to `null` below is treated the same as it being
// missing entirely, i.e. it falls through to the store default.
// Singapore's store key is `quista`, not `reebelo-sg`.
// ---------------------------------------------------------------------------

// Per-store, per-category multipliers applied to a line item's price (minus
// its buyer protection fee, if any) to derive `adjusted_value`. The category
// key is the catalog category name for PRODUCT lines, or a fixed label
// ("Warranty", "Shipping") for line item types that aren't tied to a catalog
// category.
export const ADJUSTMENT_FACTORS: Partial<
  Record<ReebeloStoreT, Record<string, number | null>>
> = {
  'reebelo-au': {
    Headphones: 0.09,
    Earphones: 0.09,
    Laptops: 0.084,
    'Phone Accessories': 0.325,
    Shipping: 0.416,
    Smartphones: 0.085,
    Smartwatches: 0.08,
    Tablets: 0.088,
    Warranty: 0.404,
  },
  'reebelo-ca': {
    Headphones: 0.082,
    Earphones: 0.082,
    Laptops: 0.057,
    'Phone Accessories': 0.292,
    Shipping: 0.199,
    Smartphones: 0.058,
    Smartwatches: 0.054,
    Tablets: 0.058,
    Warranty: 0.361,
  },
  'reebelo-nz': {
    Headphones: 0.09,
    Earphones: 0.09,
    Laptops: 0.082,
    'Phone Accessories': 0.316,
    Shipping: 0.414,
    Smartphones: 0.08,
    Smartwatches: 0.073,
    Tablets: 0.083,
    Warranty: 0.371,
  },
  quista: {
    Headphones: 0.09,
    Earphones: 0.09,
    Laptops: 0.072,
    'Phone Accessories': 0.292,
    Shipping: null,
    Smartphones: 0.07,
    Smartwatches: 0.071,
    Tablets: 0.077,
    Warranty: null,
  },
  'reebelo-uk': {
    Headphones: 0.09,
    Earphones: 0.09,
    Laptops: 0.072,
    'Phone Accessories': null,
    Shipping: null,
    Smartphones: 0.074,
    Smartwatches: 0.067,
    Tablets: 0.083,
    Warranty: null,
  },
  'reebelo-us': {
    Headphones: 0.094,
    Earphones: 0.094,
    Laptops: 0.051,
    'Phone Accessories': 0.278,
    Shipping: 0.161,
    Smartphones: 0.052,
    Smartwatches: 0.058,
    Tablets: 0.06,
    Warranty: 0.36,
  },
  // Seeded for local/dev testing only — not Data-team-approved figures.
  'reebelo-dev': {
    Smartphones: 0.1,
    Tablets: 0.15,
    Smartwatches: 0.2,
    Laptops: 0.12,
    Warranty: 0.5,
    Shipping: 0.25,
  },
};

// Per-store fallback multiplier used when a store is known but the category
// isn't (or its cell above is `null`).
export const STORE_DEFAULT_ADJUSTMENT_FACTORS: Partial<
  Record<ReebeloStoreT, number>
> = {
  'reebelo-au': 0.085,
  'reebelo-ca': 0.058,
  'reebelo-nz': 0.081,
  quista: 0.071,
  'reebelo-uk': 0.074,
  'reebelo-us': 0.054,
  // Seeded for local/dev testing only — not Data-team-approved figures.
  'reebelo-dev': 0.1,
};

// Per-category fallback multiplier used when the category is known but the
// store isn't (e.g. a new store that hasn't been added above yet).
export const CATEGORY_DEFAULT_ADJUSTMENT_FACTORS: Record<string, number> = {
  Headphones: 0.09,
  Earphones: 0.09,
  Laptops: 0.072,
  'Phone Accessories': 0.292,
  Shipping: 0.317,
  Smartphones: 0.073,
  Smartwatches: 0.071,
  Tablets: 0.077,
  Warranty: 0.373,
};

// Checkout lines carry the store-locale (translated) category name, but every
// table above is keyed by the English catalog name. Translated names are
// mapped back to English before any lookup (RT-17393); matching ignores case.
// Covers MsCatalog2's current category translations plus likely
// translations for every category above in every storefront language
// (including ones MsCatalog2 hasn't translated yet, e.g. Swedish), so a new
// translation is usually picked up without a code change. If MsCatalog2 adds
// a name not listed here, add it. Names identical to English (e.g.
// "Smartphones") need no entry.
export const CATEGORY_NAME_ALIASES: Record<string, string> = {
  // de (reebelo-de)
  Kopfhörer: 'Headphones',
  'In-Ear-Kopfhörer': 'Earphones',
  Ohrhörer: 'Earphones',
  Notebooks: 'Laptops',
  'Handy-Zubehör': 'Phone Accessories',
  Handys: 'Smartphones',
  // es (reebelo-es)
  Auriculares: 'Headphones',
  Cascos: 'Headphones',
  'Auriculares intrauditivos': 'Earphones',
  'Auriculares in-ear': 'Earphones',
  Portátiles: 'Laptops',
  'Ordenadores portátiles': 'Laptops',
  'Accesorios para teléfonos': 'Phone Accessories',
  'Accesorios para teléfono': 'Phone Accessories',
  'Accesorios para móviles': 'Phone Accessories',
  'Teléfonos inteligentes': 'Smartphones',
  Móviles: 'Smartphones',
  'Relojes inteligentes': 'Smartwatches',
  Tabletas: 'Tablets',
  // fr (reebelo-fr)
  'Casque audio': 'Headphones',
  'Casques audio': 'Headphones',
  Casque: 'Headphones',
  Casques: 'Headphones',
  Écouteurs: 'Earphones',
  'Ordinateurs portables': 'Laptops',
  'PC portables': 'Laptops',
  'Accessoires pour téléphone': 'Phone Accessories',
  'Accessoires téléphone': 'Phone Accessories',
  'Montres connectées': 'Smartwatches',
  Tablettes: 'Tablets',
  // it (reebelo-it)
  Cuffie: 'Headphones',
  Auricolari: 'Earphones',
  Laptop: 'Laptops',
  'Computer portatili': 'Laptops',
  'Accessori per telefoni': 'Phone Accessories',
  'Accessori per telefono': 'Phone Accessories',
  'Accessori per smartphone': 'Phone Accessories',
  Smartphone: 'Smartphones',
  Smartwatch: 'Smartwatches',
  Tablet: 'Tablets',
  // sv (reebelo-se) - not yet translated in MsCatalog2
  Hörlurar: 'Headphones',
  Öronsnäckor: 'Earphones',
  'In-ear-hörlurar': 'Earphones',
  'Bärbara datorer': 'Laptops',
  Mobiltillbehör: 'Phone Accessories',
  Mobiler: 'Smartphones',
  Mobiltelefoner: 'Smartphones',
  Smartklockor: 'Smartwatches',
  'Smarta klockor': 'Smartwatches',
  Surfplattor: 'Tablets',
};

// Final fallback when both the store and the category are unknown.
export const GLOBAL_DEFAULT_ADJUSTMENT_FACTOR = 0.074;

// Per-store multiplier applied to the buyer protection fee portion of a line
// item's price, carved out separately from the rest of the device value.
export const BUYER_FEE_ADJUSTMENT_FACTORS: Partial<
  Record<ReebeloStoreT, number>
> = {
  'reebelo-au': 0.909,
  'reebelo-ca': 1,
  'reebelo-nz': 0.87,
  quista: 0.935,
  'reebelo-uk': 0.833,
  'reebelo-us': 1,
  // 'reebelo-se': Buyer Protection Fee is enabled for Sweden, but its factor
  // has not been supplied by the Data team yet (RT-17307). Deliberately left
  // unset rather than guessed: until a real value is added here, reebelo-se
  // resolves to GLOBAL_DEFAULT_BUYER_FEE_ADJUSTMENT_FACTOR (1, i.e. the fee is
  // reported unadjusted), same as every other unconfigured store. Replace this
  // comment with `'reebelo-se': <factor>,` once the Data team provides it.
  // 'reebelo-ie': same situation as reebelo-se above (BPF enabled, factor not yet
  // supplied by the Data team - RT-17260). Resolves to the global default until then.
  // 'reebelo-nl': same situation as reebelo-ie above (BPF enabled, factor not yet
  // supplied by the Data team - RT-17574). Resolves to the global default until then.
  // 'reebelo-at': same situation as reebelo-nl above (BPF enabled, factor not yet
  // supplied by the Data team - RT-17698). Resolves to the global default until then.
  // 'reebelo-be', 'reebelo-pt', 'reebelo-fi': same situation as reebelo-at above (BPF enabled,
  // factor not yet supplied by the Data team - RT-17758/RT-17726/RT-17760).
};

// Fallback when the store is unknown. Buyer fee has no category dimension.
export const GLOBAL_DEFAULT_BUYER_FEE_ADJUSTMENT_FACTOR = 1;
